/**
 * The inspector footer and the status line's selection hints: the same `Action[]` (lib/actions.ts)
 * as keycap buttons plus the matching `viko` command, which copies on click. Both surfaces render
 * from `useSelection()`, so a label, key or command can only be changed in the registry.
 */
import { Copy } from 'lucide-react'
import { useState } from 'react'
import type { Action } from '../../lib/actions.js'
import { useToast } from '../ui.js'
import './action-footer.css'

export function Keycap({ children }: { children: string }) {
  return <kbd className="ak">{children}</kbd>
}

/** Copies a `viko` command; the button says what it copies. */
export function CommandEcho({ command, className }: { command: string; className?: string }) {
  const toast = useToast()
  return (
    <button
      type="button"
      className={`ak-cmd${className ? ` ${className}` : ''}`}
      aria-label={`Copy command: ${command}`}
      title="Copy this command"
      onClick={() => {
        void navigator.clipboard?.writeText(command).then(
          () => toast('Command copied'),
          () => toast('Copy failed. Select the command instead.', 'error'),
        )
      }}
    >
      <span aria-hidden="true">$</span> <code>{command}</code>
      <Copy size={13} aria-hidden="true" />
    </button>
  )
}

/** The answer bar owns the numbered answers, note, reply, delegate and later; the footer skips them. */
export const footerActions = (list: readonly Action[]) => list.filter((a) => !a.option && !a.inBar)

export function ActionFooter({ actions, title }: { actions: readonly Action[]; title?: string }) {
  const shown = footerActions(actions)
  const [hot, setHot] = useState<string>()
  if (!shown.length) return null
  const focused = shown.find((a) => a.id === hot && a.vikoCommand)
  const echo = focused ?? shown.find((a) => a.vikoCommand && !a.disabledReason)
  return (
    <div className="ak-foot" role="group" aria-label={title ? `Actions for ${title}` : 'Actions'}>
      <ul className="ak-foot__list">
        {shown.map((a) => (
          <li key={a.id}>
            <button
              type="button"
              className={`ak-act${a.danger ? ' is-danger' : ''}`}
              disabled={!!a.disabledReason}
              title={a.disabledReason}
              data-action={a.id}
              onClick={a.run}
              onMouseEnter={() => setHot(a.id)}
              onFocus={() => setHot(a.id)}
            >
              <span>{a.label}</span>
              <Keycap>{a.key}</Keycap>
            </button>
          </li>
        ))}
      </ul>
      {echo?.vikoCommand ? <CommandEcho command={echo.vikoCommand} /> : null}
    </div>
  )
}
